import React from 'react'
import { cn } from '@kiao-voice/ui-kit'

/**
 * 髮絲線即進度。進行中與完成都是墨黑，唯一的語意色是失敗的 `--error`——
 * 綠色在這套系統裡是「已完成」，一條還在跑的線上綠是說了不算數的話。
 *
 * **百分比只改 `--p`，DOM 不重建。** 進度每兩秒更新一次，若元素隨百分比重掛，
 * CSS 過渡會從頭開始，線就會一格一格跳而不是連續滑動；`key` 也因此不含百分比。
 *
 * 過渡時長走 token `--pline-transition`，與 `PROGRESS_TRANSITION_MS` 同值
 * （`progress-line.test.tsx` 釘住這件事）。inline style 只留 `--p` 一個真正
 * 隨執行時變化的值。
 */

export interface ProgressLineProps {
  /** 0–100；`null` 搭配 `indeterminate` 代表「還不知道」。 */
  percent: number | null
  /** 沒有可信的數字：掃動，而不是停在 0%（0% 讀起來是「還沒開始」）。 */
  indeterminate?: boolean
  failed?: boolean
  done?: boolean
  /** 2px 粗版，給試轉面板這種單一焦點的地方。 */
  thick?: boolean
  label: string
  className?: string
}

export function ProgressLine({
  percent,
  indeterminate = false,
  failed = false,
  done = false,
  thick = false,
  label,
  className,
}: ProgressLineProps) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      // 不定進度時**不給** aria-valuenow：報一個數字等於宣稱我們知道進度。
      aria-valuenow={indeterminate || percent === null ? undefined : percent}
      className={cn(
        'relative overflow-hidden bg-line-soft',
        thick ? 'h-0.5' : 'h-[1.5px]',
        className
      )}
      style={{ '--p': `${percent ?? 0}%` } as React.CSSProperties}
    >
      <i
        data-testid="progress-line-fill"
        className={cn(
          'block h-full w-(--p) bg-foreground transition-[width] duration-(--pline-transition) ease-linear',
          indeterminate && 'w-[34%] animate-pline-sweep transition-none',
          failed && 'bg-error transition-none',
          done && 'duration-[450ms] ease-[cubic-bezier(.2,.8,.2,1)]',
          'motion-reduce:animate-none motion-reduce:transition-none',
          indeterminate && 'motion-reduce:w-full motion-reduce:bg-fg-soft'
        )}
      />
    </div>
  )
}
